import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { MyRecipesLink } from "@/components/MyRecipesLink";

/**
 * Centralised app header. Every page should render this so the logo, nav
 * order, and CTA placement stay consistent. The logo always links home,
 * except on the home page itself (passed via `logoStatic`).
 */
type Props = {
  /** Render the logo as static text (no link). Use on the home page. */
  logoStatic?: boolean;
  /** Optional contextual back link rendered after the main nav. */
  back?: { to: string; label: string };
  /** Hide the "Clean another recipe" CTA (e.g. on the capture page). */
  hideCleanAnother?: boolean;
  /** Optional click handler for the CTA when on the capture page. */
  onCleanAnotherClick?: () => void;
};

export const AppHeader = ({
  logoStatic,
  back,
  hideCleanAnother,
  onCleanAnotherClick,
}: Props) => (
  <header className="mx-auto flex max-w-5xl items-center justify-between gap-2 px-4 py-3 sm:px-6 sm:py-6">
    <Logo asStatic={logoStatic} />
    <div className="flex items-center justify-end gap-1">
      <MyRecipesLink
        hideCleanAnother={hideCleanAnother}
        onCleanAnotherClick={onCleanAnotherClick}
      />
      {back && (
        // Mobile-only: the desktop nav already highlights the active section
        // (Menus / Shopping list), so the contextual back link is redundant
        // there. On mobile the hamburger collapses the nav, so we keep the
        // quick back link visible next to the menu icon.
        <Button variant="ghost" size="sm" asChild className="sm:hidden">
          <Link to={back.to}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {back.label}
          </Link>
        </Button>
      )}
    </div>
  </header>
);
